/**
 * Order Success Page
 * Displayed after successful order creation
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import type { OrderWithItems } from '@phoenixbill/shared/types/order';

export default function OrderSuccessPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/client/orders/${orderId}`);
      setOrder(response.data.data);
    } catch (err) {
      console.error('Failed to load order', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8 text-center">
          <p className="text-slate-400">Order not found</p>
          <button
            onClick={() => navigate('/client/orders')}
            className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', label: 'Pending' },
      active: { bg: 'bg-green-900/20', text: 'text-green-400', label: 'Active' },
      fraud: { bg: 'bg-red-900/20', text: 'text-red-400', label: 'Under Review' },
      cancelled: { bg: 'bg-slate-900/20', text: 'text-slate-400', label: 'Cancelled' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-900/20 rounded-full mb-4">
          <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-100 mb-2">Order Placed Successfully!</h1>
        <p className="text-slate-400">Thank you for your order. We'll process it shortly.</p>
      </div>

      {/* Order Details */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-100 mb-1">Order #{order.orderNumber}</h2>
            <p className="text-sm text-slate-400">
              Placed on {new Date(order.createdAt).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(order.status)}
        </div>

        {/* Status Messages */}
        {order.status === 'pending' && (
          <div className="bg-orange-900/20 border border-orange-800 rounded-lg p-4 mb-6">
            <p className="text-orange-400 text-sm">
              Your order is pending payment. Once payment is received, we'll activate your services automatically.
            </p>
          </div>
        )}

        {order.status === 'fraud' && (
          <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-6">
            <p className="text-yellow-400 text-sm">
              Your order is currently under review for security purposes. Our team will review it shortly. You'll receive an email once approved.
            </p>
          </div>
        )}

        {/* Order Items */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-100 mb-3">Order Items</h3>
          <div className="space-y-3">
            {order.items.map(item => (
              <div key={item.id} className="flex justify-between items-start bg-slate-900 rounded-lg p-4">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-100">{item.productName}</h4>
                  <p className="text-sm text-slate-400">
                    {item.billingCycle} × {item.quantity}
                  </p>
                  {item.domain && (
                    <p className="text-sm text-gray-500">Domain: {item.domain}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-100">{formatPrice(item.total)}</p>
                  {item.setupFee > 0 && (
                    <p className="text-xs text-slate-400">
                      +{formatPrice(item.setupFee)} setup
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="border-t border-slate-700/50 pt-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-slate-300">
              <span>Subtotal</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            {order.setupFeeTotal > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>Setup Fees</span>
                <span>{formatPrice(order.setupFeeTotal)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>
                  Discount
                  {order.promoCode && <span className="ml-1">({order.promoCode})</span>}
                </span>
                <span>-{formatPrice(order.discount)}</span>
              </div>
            )}
            {order.taxAmount > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>Tax ({(order.taxRate * 100).toFixed(1)}%)</span>
                <span>{formatPrice(order.taxAmount)}</span>
              </div>
            )}
            <div className="border-t border-slate-700/50 pt-2">
              <div className="flex justify-between text-lg font-semibold text-gray-100">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
        <h3 className="font-semibold text-gray-100 mb-3">Billing Address</h3>
        <div className="text-slate-300 text-sm space-y-1">
          <p>{order.firstName} {order.lastName}</p>
          {order.companyName && <p>{order.companyName}</p>}
          <p>{order.address1}</p>
          {order.address2 && <p>{order.address2}</p>}
          <p>{order.city}, {order.state} {order.postcode}</p>
          <p>{order.country}</p>
          {order.phoneNumber && <p>Phone: {order.phoneNumber}</p>}
        </div>
      </div>

      {/* Order Notes */}
      {order.notes && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <h3 className="font-semibold text-gray-100 mb-3">Order Notes</h3>
          <p className="text-slate-300 text-sm whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate('/client/orders')}
          className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold"
        >
          View All Orders
        </button>
        <button
          onClick={() => navigate('/client/products')}
          className="flex-1 px-6 py-3 bg-slate-700/50 text-gray-200 rounded-lg hover:bg-slate-600/50 transition-colors font-semibold"
        >
          Continue Shopping
        </button>
      </div>

      {/* Next Steps */}
      <div className="mt-8 bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <h3 className="font-semibold text-gray-100 mb-3">What's Next?</h3>
        <ul className="space-y-2 text-sm text-slate-300">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            You'll receive an order confirmation email shortly
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Once payment is confirmed, your services will be activated automatically
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            You can track your order status in the Orders section
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            If you have any questions, contact our support team
          </li>
        </ul>
      </div>
    </div>
  );
}
